import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Navigation, X, Car, Tent, Waves, TreePine, MapPin, GripHorizontal, ExternalLink, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CampingSpot } from '../types';

interface AutoNavOverlayProps {
  spot: CampingSpot;
  distanceM: number;
  onOpenSpot: (spot: CampingSpot) => void;
  onExit: () => void;
}

function getSatelliteTileUrls(lat: number, lon: number, zoom = 17): string[] {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return [
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x + 1}`,
  ];
}

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

function getSpotIcon(spot: CampingSpot, size = 14) {
  const type = spot.spotType || '';
  if (type === 'rasteplass' || type === 'snuplass') return <Car size={size} className="text-blue-400" />;
  if (type === 'commercial_campsite') return <Tent size={size} className="text-amber-400" />;
  if (type === 'coastal') return <Waves size={size} className="text-cyan-400" />;
  if (type === 'gapahuk') return <TreePine size={size} className="text-emerald-400" />;
  return <MapPin size={size} className="text-emerald-400" />;
}

function getLegalStatus(spot: CampingSpot): { label: string; className: string } {
  const type = spot.spotType || '';
  if (type === 'commercial_campsite') return { label: 'Campingplass', className: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
  if (type === 'rasteplass' || type === 'snuplass') return { label: 'Offentlig', className: 'text-blue-500 bg-blue-500/10 border-blue-500/20' };
  const status = spot.aiResult?.anbefaling || 'SJEKK_SKILT';
  if (status === 'LOVLIG') return { label: 'Fri tilgang', className: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' };
  if (status === 'FORBUDT') return { label: 'Forbudt', className: 'text-red-500 bg-red-500/10 border-red-500/20' };
  return { label: 'Sjekk skilt', className: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
}

const AutoNavOverlay: React.FC<AutoNavOverlayProps> = ({ spot, distanceM, onOpenSpot, onExit }) => {
  const { t } = useTranslation();

  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem('fricamp_autonav_pos');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return { x: 0, y: 0 };
  });

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const hasDraggedRef = useRef(false);

  const satelliteTiles = useMemo(() => {
    if (!spot.location) return [];
    return getSatelliteTileUrls(spot.location.latitude, spot.location.longitude, 17);
  }, [spot.location?.latitude, spot.location?.longitude]);

  // Prioriter ekte bilde foran satellitt — samme rekkefølge som feed-/mini-kortene
  // (MapMiniCards): UGC-thumbnail → første opplastede bilde → auto-hentet bilde.
  // Faller tilbake til satellitt hvis ingen finnes eller bildet ikke lastes.
  const coverPhotoUrl = spot.ugcThumbnailUrl || spot.photoUrls?.[0] || spot.auto_image_url || null;
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => { setPhotoFailed(false); }, [coverPhotoUrl]);
  const showPhoto = !!coverPhotoUrl && !photoFailed;

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button[data-nodrag]')) return;
    
    if (e.cancelable) {
      e.preventDefault();
    }
    
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
  };

  const handlePointerMove = (clientX: number, clientY: number) => {
    if (!isDraggingRef.current) return;
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;
    if (Math.hypot(dx, dy) > 2) {
      hasDraggedRef.current = true;
    }
    setPos({
      x: dragStartRef.current.posX + dx,
      y: dragStartRef.current.posY + dy,
    });
  };

  const handlePointerUp = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    try { localStorage.setItem('fricamp_autonav_pos', JSON.stringify(pos)); } catch {}
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => handlePointerMove(e.clientX, e.clientY);
    const onUp = () => handlePointerUp();

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [pos.x, pos.y]);

  const handleClickCapture = (e: React.MouseEvent) => {
    if (hasDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleClick = () => {
    if (hasDraggedRef.current) return;
    onOpenSpot(spot);
  };

  const legal = getLegalStatus(spot);

  return (
    <div
      className="fixed top-[calc(env(safe-area-inset-top,0px)+96px)] left-4 md:left-[72px] z-[9997] animate-in fade-in duration-200 select-none touch-none cursor-grab active:cursor-grabbing"
      style={{ transform: `translate3d(${pos.x}px, ${pos.y}px, 0)` }}
      role="status"
      aria-live="polite"
      onPointerDown={handlePointerDown}
      onClickCapture={handleClickCapture}
      onDragStart={(e) => e.preventDefault()}
    >
      <div
        onClick={handleClick}
        className="bg-[var(--bg-surface)]/95 backdrop-blur-md border border-emerald-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col w-[340px] max-w-[90vw] transition-shadow hover:shadow-emerald-500/10"
      >
        {/* Top bar / Drag handle */}
        <div className="bg-emerald-500/10 px-3 py-2 border-b border-emerald-500/20 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Navigation size={14} className="text-emerald-400 animate-pulse" />
            <span className="text-2xs font-black uppercase tracking-widest text-emerald-400 leading-none">
              Auto-navigasjon
            </span>
          </div>
          <div className="flex items-center gap-2">
            <GripHorizontal size={16} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors" />
            <button
              data-nodrag="true"
              onClick={(e) => { e.stopPropagation(); onExit(); }}
              aria-label="Avslutt auto-navigasjon"
              className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0 active:scale-95 transition-all rounded-lg hover:bg-[var(--bg-surface-alt)]"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Main Content mimicking FollowSpotOverlay expanded view */}
        <div className="relative w-full h-48 bg-stone-800 overflow-hidden cursor-pointer group">
          {showPhoto ? (
            /* Ekte plassbilde (prioritert foran satellitt) */
            <img
              src={coverPhotoUrl!}
              alt={spot.name || ''}
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
              loading="eager"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <>
              {/* Satellite tiles (fallback) */}
              <div className="flex w-[200%] h-full" style={{ marginLeft: '-25%' }}>
                {satelliteTiles.map((url, i) => (
                  <img key={i} src={url} alt="" className="h-full flex-1 object-cover pointer-events-none" draggable={false} loading="eager" />
                ))}
              </div>

              {/* Overlaid spot marker right in the center — kun meningsfullt på satellittkartet */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-red-500/30 animate-ping pointer-events-none" style={{ margin: '-8px' }} />
                <div className="absolute inset-0 rounded-full bg-red-500/20 border-2 border-red-400/50 pointer-events-none" style={{ margin: '-4px' }} />
                <div className="w-8 h-8 rounded-full bg-red-50 border-2 border-white shadow-lg flex items-center justify-center text-white transition-transform group-hover:scale-110">
                  {getSpotIcon(spot, 16)}
                </div>
              </div>
            </>
          )}

          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />

          {/* Top-left: legal status badge */}
          <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm border border-white/10">
            {getSpotIcon(spot, 12)}
            <span className={`text-3xs font-bold px-1.5 py-0.5 rounded border ${legal.className}`}>
              {legal.label}
            </span>
          </div>

          {/* Bottom overlay: name, rating, distance */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 pt-6 pointer-events-none">
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] font-black text-white truncate flex-1 drop-shadow-lg">
                {spot.name || 'Ukjent plass'}
              </span>
              {spot.isVerified && <Star size={14} className="text-amber-400 shrink-0 drop-shadow" fill="currentColor" />}
            </div>
            <div className="flex items-center gap-2.5 mt-0.5">
              {(spot.score ?? 0) > 0 && (
                <span className="flex items-center gap-0.5 text-tiny font-bold text-amber-400 drop-shadow">
                  <Star size={10} fill="currentColor" /> {spot.score?.toFixed(1)}
                </span>
              )}
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0 shadow-sm"
                style={{ backgroundColor: spot.suitabilityColor || '#22c55e' }}
              />
              <span className="text-tiny font-semibold text-white/90 drop-shadow">
                {formatDistance(distanceM)} unna
              </span>
              {(spot.reviewCount ?? 0) > 0 && (
                <span className="flex items-center gap-0.5 text-2xs font-semibold text-white/70 drop-shadow">
                  <Star size={8} className="text-amber-400" /> {spot.reviewCount}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="bg-[var(--bg-surface)] px-3 py-2.5 flex items-center justify-between border-t border-[var(--border-primary)]">
          <span className="text-tiny font-semibold text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-colors">
            {t("auto.map.autonavoverlay.klikk_for_info", "Klikk for å åpne spot card")}
          </span>
          <button
            data-nodrag="true"
            onClick={(e) => { e.stopPropagation(); onOpenSpot(spot); }}
            className="px-3 py-1.5 rounded-xl bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-primary)] text-[var(--text-primary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95 flex items-center gap-1.5 shadow-sm"
          >
            <ExternalLink size={12} className="text-emerald-500" /> {t("auto.map.followspotoverlay.detaljer", "Detaljer")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoNavOverlay;
